/**
 * Password Generator Service
 * Provides secure password generation and validation functionality
 * Implements cryptographically secure random generation and complexity validation
 */
class PasswordGenerator {
  constructor() {
    // Character sets for password generation
    this.charSets = {
      lowercase: "abcdefghijklmnopqrstuvwxyz",
      uppercase: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
      numbers: "0123456789",
      symbols: "!@#$%^&*()_+-=[]{}|;:,.<>?",
    };

    // Default password requirements
    this.requirements = {
      minLength: 12,
      maxLength: 128,
      requireLowercase: true,
      requireUppercase: true,
      requireNumbers: true,
      requireSymbols: true,
      minSymbols: 2,
      minNumbers: 2,
    };
  }

  /**
   * Generates a cryptographically secure random password
   * @param {number} length - Password length (default: 12, min: 8, max: 128)
   * @param {Object} options - Password generation options
   * @returns {string} - Generated secure password
   * @throws {Error} - If length is invalid or generation fails
   */
  generateSecurePassword(length = 12, options = {}) {
    try {
      // Validate length
      if (length < 8 || length > 128) {
        throw new Error("Password length must be between 8 and 128 characters");
      }

      // Merge options with defaults
      const opts = {
        includeLowercase: true,
        includeUppercase: true,
        includeNumbers: true,
        includeSymbols: true,
        excludeSimilar: false, // Exclude similar looking characters (0, O, l, 1, etc.)
        ...options,
      };

      // Build character set based on options
      let charset = "";
      const requiredChars = [];

      if (opts.includeLowercase) {
        const chars = opts.excludeSimilar
          ? this.charSets.lowercase.replace(/[il]/g, "")
          : this.charSets.lowercase;
        charset += chars;
        requiredChars.push(this.getSecureRandomChar(chars));
      }

      if (opts.includeUppercase) {
        const chars = opts.excludeSimilar
          ? this.charSets.uppercase.replace(/[IOL]/g, "")
          : this.charSets.uppercase;
        charset += chars;
        requiredChars.push(this.getSecureRandomChar(chars));
      }

      if (opts.includeNumbers) {
        const chars = opts.excludeSimilar
          ? this.charSets.numbers.replace(/[01]/g, "")
          : this.charSets.numbers;
        charset += chars;
        requiredChars.push(this.getSecureRandomChar(chars));
      }

      if (opts.includeSymbols) {
        charset += this.charSets.symbols;
        requiredChars.push(this.getSecureRandomChar(this.charSets.symbols));
      }

      if (charset.length === 0) {
        throw new Error("At least one character type must be included");
      }

      // Generate password ensuring required characters are included
      let password = requiredChars.join("");

      // Fill remaining length with random characters
      for (let i = password.length; i < length; i++) {
        password += this.getSecureRandomChar(charset);
      }

      // Shuffle the password using Fisher-Yates algorithm for better randomness
      return this.shuffleString(password);
    } catch (error) {
      throw new Error(`Password generation failed: ${error.message}`);
    }
  }

  /**
   * Gets a cryptographically secure random character from a character set
   * @param {string} charset - Character set to choose from
   * @returns {string} - Random character
   */
  getSecureRandomChar(charset) {
    if (typeof crypto !== "undefined" && crypto.getRandomValues) {
      // Use Web Crypto API for secure random generation
      const array = new Uint32Array(1);
      crypto.getRandomValues(array);
      return charset[array[0] % charset.length];
    } else if (typeof require !== "undefined") {
      // Node.js environment - use crypto module
      try {
        const crypto = require("crypto");
        const randomBytes = crypto.randomBytes(4);
        const randomValue = randomBytes.readUInt32BE(0);
        return charset[randomValue % charset.length];
      } catch (error) {
        // Fallback to Math.random if crypto is not available
        console.warn(
          "Crypto module not available, falling back to Math.random",
        );
        return charset[Math.floor(Math.random() * charset.length)];
      }
    } else {
      // Fallback to Math.random (less secure)
      console.warn("Secure random generation not available, using Math.random");
      return charset[Math.floor(Math.random() * charset.length)];
    }
  }

  /**
   * Shuffles a string using Fisher-Yates algorithm
   * @param {string} str - String to shuffle
   * @returns {string} - Shuffled string
   */
  shuffleString(str) {
    const array = str.split("");
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array.join("");
  }

  /**
   * Validates password strength against complexity requirements
   * @param {string} password - Password to validate
   * @param {Object} customRequirements - Custom requirements (optional)
   * @returns {Object} - Validation result with detailed feedback
   */
  validatePasswordStrength(password, customRequirements = {}) {
    try {
      if (!password || typeof password !== "string") {
        return {
          isValid: false,
          score: 0,
          feedback: "Password is required",
          requirements: {},
        };
      }

      // Merge custom requirements with defaults
      const reqs = { ...this.requirements, ...customRequirements };

      // Check individual requirements
      const checks = {
        minLength: password.length >= reqs.minLength,
        maxLength: password.length <= reqs.maxLength,
        hasLowercase: reqs.requireLowercase ? /[a-z]/.test(password) : true,
        hasUppercase: reqs.requireUppercase ? /[A-Z]/.test(password) : true,
        hasNumbers: reqs.requireNumbers ? /\d/.test(password) : true,
        hasSymbols: reqs.requireSymbols
          ? /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)
          : true,
        minSymbols: reqs.requireSymbols
          ? (password.match(/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/g) || []).length >=
            reqs.minSymbols
          : true,
        minNumbers: reqs.requireNumbers
          ? (password.match(/\d/g) || []).length >= reqs.minNumbers
          : true,
      };

      // Calculate strength score (0-100)
      const passedChecks = Object.values(checks).filter(Boolean).length;
      const totalChecks = Object.keys(checks).length;
      const score = Math.round((passedChecks / totalChecks) * 100);

      // Determine overall validity
      const isValid = Object.values(checks).every(Boolean);

      // Generate feedback
      const feedback = this.generatePasswordFeedback(checks, reqs);

      return {
        isValid,
        score,
        feedback,
        requirements: checks,
        details: {
          length: password.length,
          hasLowercase: /[a-z]/.test(password),
          hasUppercase: /[A-Z]/.test(password),
          hasNumbers: /\d/.test(password),
          hasSymbols: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password),
          symbolCount: (
            password.match(/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/g) || []
          ).length,
          numberCount: (password.match(/\d/g) || []).length,
        },
      };
    } catch (error) {
      return {
        isValid: false,
        score: 0,
        feedback: `Validation error: ${error.message}`,
        requirements: {},
      };
    }
  }

  /**
   * Generates human-readable feedback for password validation
   * @param {Object} checks - Validation check results
   * @param {Object} requirements - Password requirements
   * @returns {string} - Feedback message
   */
  generatePasswordFeedback(checks, requirements) {
    const issues = [];

    if (!checks.minLength) {
      issues.push(
        `Password must be at least ${requirements.minLength} characters long`,
      );
    }
    if (!checks.maxLength) {
      issues.push(
        `Password must be no more than ${requirements.maxLength} characters long`,
      );
    }
    if (!checks.hasLowercase) {
      issues.push("Password must contain at least one lowercase letter");
    }
    if (!checks.hasUppercase) {
      issues.push("Password must contain at least one uppercase letter");
    }
    if (!checks.hasNumbers) {
      issues.push("Password must contain at least one number");
    }
    if (!checks.hasSymbols) {
      issues.push("Password must contain at least one special character");
    }
    if (!checks.minSymbols) {
      issues.push(
        `Password must contain at least ${requirements.minSymbols} special characters`,
      );
    }
    if (!checks.minNumbers) {
      issues.push(
        `Password must contain at least ${requirements.minNumbers} numbers`,
      );
    }

    if (issues.length === 0) {
      return "Password meets all requirements";
    }

    return issues.join(". ");
  }

  /**
   * Checks if a password meets minimum security requirements
   * @param {string} password - Password to check
   * @returns {boolean} - True if password is secure enough
   */
  isPasswordSecure(password) {
    const validation = this.validatePasswordStrength(password);
    return validation.isValid && validation.score >= 80;
  }

  /**
   * Generates multiple password options for user selection
   * @param {number} count - Number of passwords to generate (default: 3)
   * @param {number} length - Password length (default: 12)
   * @param {Object} options - Generation options
   * @returns {Array<string>} - Array of generated passwords
   */
  generatePasswordOptions(count = 3, length = 12, options = {}) {
    const passwords = [];
    for (let i = 0; i < count; i++) {
      passwords.push(this.generateSecurePassword(length, options));
    }
    return passwords;
  }
}

export default new PasswordGenerator();
